import { useMemo, useState } from 'react';
import { BioLinkAutoAddResult, DEFAULT_BIO_LINK_SETTINGS } from 'fym-shared/src/smartlinks/bioLinkSettings';
import { useGetBioLinkSettingsQuery } from '../../../../redux/api/fymAPI';
import { useIsDemoAccount } from '../../../accountSelector/useIsDemoAccount';

export interface BioLinkTick {
  /** False for the demo artist and for an account with no Bio Link page. */
  isShown: boolean;
  /** The tick of this song. */
  isOn: boolean;
  /** True when the Bio Link setting "Add new songs automatically" is off. */
  isOffBySetting: boolean;
  setOn: (isOn: boolean) => void;
  /** The `addToBioLink` value of the add-song call. Undefined when the tick is not shown (the server decides). */
  addToBioLink: boolean | undefined;
}

/**
 * "Auto add to my Bio Link" of the add-song flow (design smartlink-editor e1, e5). The default is the
 * Bio Link setting `autoAddSongs`. It is on while the setting loads, and on when an older API has no
 * setting. The tick changes this song only.
 */
export function useBioLinkTick(accountId: number): BioLinkTick {
  const isDemo = useIsDemoAccount();
  const settings = useGetBioLinkSettingsQuery({ accountId }, { skip: isDemo });
  const [choice, setOn] = useState<boolean>();
  const autoAddSongs = settings.data?.settings.autoAddSongs ?? DEFAULT_BIO_LINK_SETTINGS.autoAddSongs;
  const hasNoBioLink = settings.data !== undefined && settings.data.bioLinkSmartLinkId === null;
  const isShown = !isDemo && !hasNoBioLink;
  const isOn = choice ?? autoAddSongs;
  return useMemo(
    () => ({ isShown, isOn, isOffBySetting: !autoAddSongs, setOn, addToBioLink: isShown ? isOn : undefined }),
    [autoAddSongs, isOn, isShown]
  );
}

/** The card fact "Bio Link" (design smartlink-editor e1, e2): "Adds on save", then "Added". */
export function bioLinkFactOf(
  item: { uuid: string } | undefined,
  tick: { isShown: boolean; isOn: boolean },
  result: BioLinkAutoAddResult | undefined
): 'onSave' | 'added' | undefined {
  if (item === undefined) {
    return tick.isShown && tick.isOn ? 'onSave' : undefined;
  }
  return result?.added === true ? 'added' : undefined;
}
